/**
 * Keyboard placement model (pure). The Board component wires these to key
 * events; keeping the rules here makes them unit-testable (keyboard.test.ts).
 *
 *  - Arrow keys move the cursor (clamped to the board).
 *  - Space toggles the typing direction (→ / ↓).
 *  - A letter places the first matching rack tile (in local rack order) at the
 *    first free square from the cursor, falling back to a blank auto-lettered
 *    with that letter; the cursor then advances along the typing direction,
 *    skipping squares that are already occupied.
 *  - Backspace recalls the most recently placed draft tile and moves the
 *    cursor back onto its square.
 */
import { BOARD_SIZE } from '@wordloom/engine';
import type { Board, Coord, Direction, Tile } from '@wordloom/engine';
import { isSquareFree, visibleRackIds, type DraftPlacement } from './draft';

export interface KeyboardContext {
  board: Board;
  placements: readonly DraftPlacement[];
  /** The player's rack tiles (server data). */
  rack: readonly Tile[];
  /** Local rack order (ids). */
  rackOrder: readonly string[];
  cursor: Coord;
  direction: Direction;
}

export type TypeLetterResult =
  | { ok: true; placement: DraftPlacement; cursor: Coord; usedBlank: boolean }
  | { ok: false; reason: 'not-a-letter' | 'no-tile' | 'no-space' };

const inBoard = (row: number, col: number) => row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE;

/** One step along `direction` (or backwards with delta −1); null past the edge. */
export function step(c: Coord, direction: Direction, delta: 1 | -1 = 1): Coord | null {
  const next = direction === 'H' ? { row: c.row, col: c.col + delta } : { row: c.row + delta, col: c.col };
  return inBoard(next.row, next.col) ? next : null;
}

/**
 * First free square walking from `from` along `direction`. With `inclusive`,
 * `from` itself is a candidate; otherwise the walk starts one step later.
 */
export function findFree(
  board: Board,
  placements: readonly DraftPlacement[],
  from: Coord,
  direction: Direction,
  inclusive: boolean,
): Coord | null {
  let c: Coord | null = inclusive ? from : step(from, direction);
  while (c) {
    if (isSquareFree(board, placements, c.row, c.col)) return c;
    c = step(c, direction);
  }
  return null;
}

/**
 * The rack tile to use for `letter`: the first non-blank tile with that letter
 * in local rack order, else the first blank. Placed tiles are not available.
 */
export function pickTileForLetter(
  rack: readonly Tile[],
  rackOrder: readonly string[],
  placements: readonly DraftPlacement[],
  letter: string,
): { tile: Tile; blank: boolean } | null {
  const byId = new Map(rack.map((t) => [t.id, t]));
  // Local order first; then any server tiles the local order doesn't know yet.
  const ordered = visibleRackIds(rackOrder, placements)
    .map((id) => byId.get(id))
    .filter((t): t is Tile => t !== undefined);
  const placed = new Set(placements.map((p) => p.tileId));
  for (const t of rack) if (!placed.has(t.id) && !ordered.includes(t)) ordered.push(t);

  const exact = ordered.find((t) => !t.blank && t.letter === letter);
  if (exact) return { tile: exact, blank: false };
  const blank = ordered.find((t) => t.blank);
  if (blank) return { tile: blank, blank: true };
  return null;
}

export function typeLetter(ctx: KeyboardContext, key: string): TypeLetterResult {
  const letter = key.toUpperCase();
  if (!/^[A-Z]$/.test(letter)) return { ok: false, reason: 'not-a-letter' };

  const target = findFree(ctx.board, ctx.placements, ctx.cursor, ctx.direction, true);
  if (!target) return { ok: false, reason: 'no-space' };

  const pick = pickTileForLetter(ctx.rack, ctx.rackOrder, ctx.placements, letter);
  if (!pick) return { ok: false, reason: 'no-tile' };

  const placement: DraftPlacement = { tileId: pick.tile.id, row: target.row, col: target.col };
  if (pick.blank) placement.letter = letter;

  // Advance past the placed square, skipping occupied squares. At the edge the
  // cursor stays on the square just filled.
  const after = [...ctx.placements, placement];
  const cursor = findFree(ctx.board, after, target, ctx.direction, false) ?? target;
  return { ok: true, placement, cursor, usedBlank: pick.blank };
}

/** Recall the most recently placed draft tile; the cursor moves onto its square. */
export function backspace(ctx: Pick<KeyboardContext, 'placements'>): { tileId: string; cursor: Coord } | null {
  const last = ctx.placements[ctx.placements.length - 1];
  if (!last) return null;
  return { tileId: last.tileId, cursor: { row: last.row, col: last.col } };
}

export type ArrowKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';

export function isArrowKey(key: string): key is ArrowKey {
  return key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight';
}

/** Move the cursor one square, clamped to the board. */
export function moveCursor(cursor: Coord, key: ArrowKey): Coord {
  const clamp = (n: number) => Math.max(0, Math.min(BOARD_SIZE - 1, n));
  switch (key) {
    case 'ArrowUp':
      return { row: clamp(cursor.row - 1), col: cursor.col };
    case 'ArrowDown':
      return { row: clamp(cursor.row + 1), col: cursor.col };
    case 'ArrowLeft':
      return { row: cursor.row, col: clamp(cursor.col - 1) };
    case 'ArrowRight':
      return { row: cursor.row, col: clamp(cursor.col + 1) };
  }
}

export function toggleDirection(direction: Direction): Direction {
  return direction === 'H' ? 'V' : 'H';
}
